// Client-safe types for the publisher-ads slice on the app detail page.
// The server composes these from the ads/ad_apps/ad_creatives/ad_snapshots
// tables and never forwards raw scrape payloads to the browser.

export type AdPlatform = 'meta' | 'google' | 'apple';

/** The three libraries collected today. Any other value is not a filter. */
export const AD_PLATFORMS: readonly AdPlatform[] = ['meta', 'google', 'apple'];

/** Brand dot colours for the neutral platform chip. One definition, used wherever a chip is drawn. */
export const PLATFORM_DOT: Record<AdPlatform, string> = {
  meta: 'bg-[#0866FF]',
  google: 'bg-[#34A853]',
  apple: 'bg-neutral-300',
};

/** What each ad library is called on screen. Carries tiktok because the app header still shows it. */
export const AD_LIBRARY_LABEL: Record<string, string> = {
  meta: 'Meta',
  google: 'Google',
  apple: 'Apple Search Ads',
  tiktok: 'TikTok',
};

export function isAdPlatform(value: string | null | undefined): value is AdPlatform {
  return value != null && (AD_PLATFORMS as readonly string[]).includes(value);
}

export type AppAdCreative = {
  /** ad_creatives row id — used by the same-origin creative proxy fallback. */
  id: string;
  url: string;
  role: 'image' | 'preview' | 'card_image';
};

export type AppAd = {
  id: string;
  platform: AdPlatform;
  /** As of scrapedAt — the library said the ad was still running then. */
  isActive: boolean;
  scrapedAt: string | null;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  title: string | null;
  body: string | null;
  ctaText: string | null;
  /** Sanitized to http(s) server-side; null otherwise. */
  linkUrl: string | null;
  /** Registrable host of linkUrl, for display ("flo.health"). */
  landingDomain: string | null;
  /**
   * Public ad-library page. Meta/Google: deep link to this exact ad.
   * Apple: the Ad Repository search page — Apple's repository has no
   * deep-linkable URLs (state lives in in-page GraphQL), so this is the
   * closest possible source link.
   */
  libraryUrl: string | null;
  /** Humanized creative format: "Text ad", "Video ad", "Icon ad", … */
  format: string | null;
  /** Meta: where the ad served (Facebook, Instagram, Threads, …). */
  channels: string[];
  /** Apple: App Store placements (Search results, Today tab, …). */
  placements: string[];
  /** Apple: storefront countries this creative ran in (from merged variants). */
  countries: string[];
  /** Google: number of regions the ad served in. */
  regionCount: number | null;
  /** How many near-identical library entries were merged into this card. */
  variantCount: number;
  /** Latest EU DSA total reach, when the ad has real audience data. */
  euReach: number | null;
  /** The ad has video variants (we surface its poster frame as the thumb). */
  hasVideo: boolean;
  /** Playable video file (stored copy first, platform link as fallback); the lightbox autoplays it. */
  videoUrl: string | null;
  /** Best displayable creative, if any. */
  thumb: AppAdCreative | null;
  /** All displayable creatives (capped), powers the lightbox. */
  creatives: AppAdCreative[];
};

export type AppAdsPageMeta = {
  offset: number;
  limit: number;
  /** Offset to request for the next page; equals total when exhausted. */
  nextOffset: number;
  hasMore: boolean;
};

export type AppAdsResult = {
  available: boolean;
  /** Total library entries for this app across ALL pages (pre-merge). */
  total: number;
  /** Whole-set library-entry counts per platform (not just this page). */
  counts: Record<AdPlatform, number>;
  /** This page's ads, merged within the page. */
  ads: AppAd[];
  meta: AppAdsPageMeta;
};

export const EMPTY_APP_ADS: AppAdsResult = {
  available: false,
  total: 0,
  counts: { meta: 0, google: 0, apple: 0 },
  ads: [],
  meta: { offset: 0, limit: 0, nextOffset: 0, hasMore: false },
};
